import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    Input,
    OnDestroy,
    signal,
} from '@angular/core';
import { ButtonDirective } from '@coreui/angular-pro';
import { IconModule } from '@coreui/icons-angular';
import { TranslateDirective } from 'i18n';
import { fromEvent, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';

/**
 * Velocity mode comparison
 *
 * | Mode           | Pros                             | Cons                            |
 * |----------------|----------------------------------|---------------------------------|
 * | 'exponential'  | Smooth, low CPU, no jitter       | Slight lag, under-reports peaks |
 * | 'ring-buffer'  | Instant, precise, no smoothing   | Can jitter, higher CPU          |
 *
 * Default: 'exponential'
 */
type VelocityMode = 'ring-buffer' | 'exponential';

@Component({
    selector: 'adaptive-button',
    templateUrl: './adaptive-button.component.html',
    styleUrl: './adaptive-button.component.scss',
    host: {
        class: 'adaptive-button',
        '[class.expanded]': 'state() === "expanded"',
        '[class.compact]': 'state() === "compact"',
    },
    imports: [IconModule, TranslateDirective, ButtonDirective],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdaptiveButtonComponent implements AfterViewInit, OnDestroy {
    @Input() label = 'Create';
    @Input() icon = 'cilPlus';
    @Input() color: string = 'var(--cui-primary)';
    @Input() scrollTarget: ElementRef<HTMLElement> | HTMLElement | Window | null = null;

    state = signal<'expanded' | 'compact'>('expanded');

    private element = inject(ElementRef).nativeElement as HTMLElement;

    private readonly VELOCITY_MODE: VelocityMode = 'exponential';

    private readonly TOP_THRESHOLD = 30;
    private readonly THROTTLE_MS = 80;

    private readonly THRESHOLDS = {
        'ring-buffer': { scroll: 0.25, touch: 0.20 },
        'exponential': { scroll: 0.15, touch: 0.10 },
    } as const;

    private get threshold() {
        return this.THRESHOLDS[this.VELOCITY_MODE];
    }

    // Touch priority system
    private lastVelocityTime = 0;
    private lastVelocitySource: 'touch' | 'scroll' | null = null;
    private readonly TOUCH_PRIORITY_MS = 150; // touch wins for 150ms after lift

    // Ring buffer
    private velocityBuffer: number[] = [];
    private readonly BUFFER_SIZE = 5;

    private pushRing(v: number): void {
        this.velocityBuffer.push(v);
        if (this.velocityBuffer.length > this.BUFFER_SIZE) this.velocityBuffer.shift();
    }

    private getRingVelocity(): number {
        if (this.velocityBuffer.length === 0) return 0;
        return this.velocityBuffer.reduce((a, b) => a + b, 0) / this.velocityBuffer.length;
    }

    private resetRing(): void {
        this.velocityBuffer = [];
    }

    // Exponential smoothing
    private smoothedVelocity = 0;
    private readonly ALPHA = 0.22;

    private updateExp(instant: number): number {
        this.smoothedVelocity = this.smoothedVelocity
            ? this.smoothedVelocity * (1 - this.ALPHA) + instant * this.ALPHA
            : instant;
        return this.smoothedVelocity;
    }

    // Unified velocity input with touch priority
    private acceptVelocity(instant: number, source: 'touch' | 'scroll'): void {
        const now = performance.now();

        const touchIsActive = source === 'touch';
        const timeSinceLastTouch = this.lastVelocitySource === 'touch'
            ? now - this.lastVelocityTime
            : Infinity;

        const touchHasPriority = touchIsActive || timeSinceLastTouch < this.TOUCH_PRIORITY_MS;

        // accept velocity if it is from touch (active or recent) or no recent touch: then scroll is allowed
        if (touchHasPriority || this.lastVelocitySource !== 'touch') {
            this.lastVelocityTime = now;
            this.lastVelocitySource = source;

            if (this.VELOCITY_MODE === 'ring-buffer') {
                this.pushRing(instant);
            } else {
                this.updateExp(instant);
            }
        }
    }

    private get velocity(): number {
        return this.VELOCITY_MODE === 'ring-buffer'
            ? this.getRingVelocity()
            : this.smoothedVelocity;
    }

    private resetVelocity(): void {
        if (this.VELOCITY_MODE === 'ring-buffer') {
            this.resetRing();
        } else {
            this.smoothedVelocity = 0;
        }
        this.lastVelocitySource = null;
    }

    // Scroll and touch
    private scrollContainer: Element | Window | null = null;
    private scrollSubscription?: Subscription;
    private mutationObserver?: MutationObserver;

    private lastScrollTop = 0;
    private lastScrollTime = performance.now();

    private touchStartY = 0;
    private touchStartTime = 0;

    ngAfterViewInit(): void {
        this.applyColor();
        this.attachToScrollTarget();
        this.setupTouchListeners();

        this.mutationObserver = new MutationObserver(() => {
            if (!this.scrollContainer) this.attachToScrollTarget();
        });
        this.mutationObserver.observe(document.body, { childList: true, subtree: true });
    }

    private attachToScrollTarget(): void {
        let target = this.scrollTarget instanceof ElementRef
                ? this.scrollTarget.nativeElement
                : this.scrollTarget
        if (!target) {
            target = this.findNearestScrollableParent(this.element);
        }
        
        if (target === this.scrollContainer) return;
        this.scrollSubscription?.unsubscribe();
        this.scrollContainer = target === document.documentElement ? window : target;
        this.scrollSubscription = fromEvent(this.scrollContainer, 'scroll')
            .pipe(throttleTime(this.THROTTLE_MS, undefined, { leading: true, trailing: true }))
            .subscribe(() => this.onScroll());
        this.resetVelocity();
        this.onScroll();
    }

    private onScroll(): void {
        if (!this.scrollContainer) return;

        const now = performance.now();
        const scrollTop =
            this.scrollContainer instanceof Window
                ? window.pageYOffset || document.documentElement.scrollTop
                : (this.scrollContainer as HTMLElement).scrollTop;

        const deltaTime = now - this.lastScrollTime || 1;
        const deltaScroll = scrollTop - this.lastScrollTop;
        const velocity = deltaScroll / deltaTime;

        this.acceptVelocity(velocity, 'scroll'); // ← now respects touch priority

        this.lastScrollTop = scrollTop;
        this.lastScrollTime = now;

        this.updateState(scrollTop);
    }

    private setupTouchListeners(): void {
        this.element.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.element.addEventListener('touchmove', this.onTouchMove, { passive: true });
        this.element.addEventListener('touchend', this.onTouchEnd);
        this.element.addEventListener('touchcancel', this.onTouchEnd);
    }

    private onTouchStart = (e: TouchEvent) => {
        this.touchStartY = e.touches[0].clientY;
        this.touchStartTime = performance.now();
        this.resetVelocity(); // fresh gesture
    };

    private onTouchMove = (e: TouchEvent) => {
        const deltaY = this.touchStartY - e.touches[0].clientY; // + = swipe down
        const deltaTime = performance.now() - this.touchStartTime || 1;
        this.acceptVelocity(deltaY / deltaTime, 'touch'); // touch always wins while moving
    };

    private onTouchEnd = () => {
        const v = this.velocity;
        if (Math.abs(v) > this.threshold.touch) {
            this.state.set(v > 0 ? 'compact' : 'expanded');
        }
        // do not reset velocity reset here
        // let (inertia driven) scroll continue from current value 
    };

    private updateState(scrollTop: number): void {
        if (scrollTop <= this.TOP_THRESHOLD) {
            this.state.set('expanded');
            return;
        }

        const velocity = this.velocity;
        if (Math.abs(velocity) > this.threshold.scroll) {
            this.state.set(velocity > 0 ? 'compact' : 'expanded');
        }
    }

    private findNearestScrollableParent(el: HTMLElement): HTMLElement {
        let current: HTMLElement | null = el.parentElement;
        while (current && current !== document.body) {
            const style = getComputedStyle(current);
            if (/(auto|scroll|overlay)/.test(style.overflowY)) return current;
            current = current.parentElement;
        }
        return document.documentElement;
    }

    private applyColor(): void {
        const btn = this.element.querySelector('.button') as HTMLElement;
        if (btn) btn.style.setProperty('--btn-bg', this.color);
    }

    onClick(): void { }

    ngOnDestroy(): void {
        this.scrollSubscription?.unsubscribe();
        this.mutationObserver?.disconnect();
        this.element.removeEventListener('touchstart', this.onTouchStart);
        this.element.removeEventListener('touchmove', this.onTouchMove);
        this.element.removeEventListener('touchend', this.onTouchEnd);
        this.element.removeEventListener('touchcancel', this.onTouchEnd);
    }
}